import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "src/components";
import Loader from "src/components/ui/loader.component";
import AddEditCluster from "./add-edit-cluster-form.component";
import useGetClusterList from "src/hooks/useGetClusterList";
import { getAllClusterDetails, getAllCompanyDetails, getClusterFromCompany } from "src/services/company-api";
import { clusterActions } from "src/context/cluster-slice";
import { RootState } from "src/context/store";

const ClusterLayoutComponent = ({ children }: { children: React.ReactNode }) => {
    const dispatch = useDispatch();
    const { showAddEditForm, companyList } = useSelector((state: RootState) => state.cluster);
    const { data, loading, refetch, error } = useGetClusterList();

    const loadCompanyDetails = useCallback(async () => {
        try {
            const response = await getAllCompanyDetails();
            const companyData = response?.data;
            if (!Array.isArray(companyData)) throw new Error("Invalid company data");

            const formattedCompanies = companyData?.map((item: any) => ({
                value: item.code,
                label: item.name,
            }));
            dispatch(clusterActions.setCompanyList(formattedCompanies));
        } catch (err) {
            console.error("Error fetching company data:", err);
        }
    }, [dispatch]);

    useEffect(() => {
        if (error || !data) return;

        dispatch(clusterActions.setclusterMasterTable(data));
        loadCompanyDetails();
    }, [data, error, dispatch, loadCompanyDetails]);

    const handleCompanyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCompanyCode = e.target.value;
        if (!selectedCompanyCode) return;

        try {
            const response = selectedCompanyCode == "ALL" ? await getAllClusterDetails() : await getClusterFromCompany(selectedCompanyCode);
            const clusters = response?.data;

            if (!Array.isArray(clusters)) throw new Error("Invalid cluster data");

            dispatch(clusterActions.setclusterMasterTable(clusters));
        } catch (err) {
            console.error("Failed to fetch cluster data:", err);
            dispatch(clusterActions.setclusterMasterTable([]));
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="w-full">
            <section className="w-9/12 md:8/12">
                {showAddEditForm && (
                    <AddEditCluster
                        refetch={refetch}
                        setShowAddForm={(value: boolean) => dispatch(clusterActions.setShowAddEditForm(value))}
                    />
                )}
            </section>

            <header className="flex items-center justify-between border-b-[1px] border-b-[#E9E9E9]">
                <div>
                    <h1 className="text-[#49484C] font-bold text-[2rem]">Cluster</h1>
                    <p className="text-[#767679] text-base mb-2">
                        View and manage all your cluster configurations
                    </p>
                </div>

                <div className="flex items-center gap-4">
                    <select
                        className="border border-gray-300 rounded px-3 py-2 text-sm text-gray-700"
                        onChange={handleCompanyChange}
                        defaultValue=""
                    >
                        <option value="">Select Companies</option>
                        <option value="ALL">All Companies</option>
                        {companyList?.map((company) => (
                            <option key={company.value} value={company.value}>
                                {company.label}
                            </option>
                        ))}
                    </select>

                    <Button
                        onClick={() => {
                            dispatch(clusterActions.setShowAddEditForm(true));
                            dispatch(clusterActions.setIsEdit(false));
                        }}
                        className="bg-[#7F56D9] text-white h-11 w-[15rem]"
                    >
                        Add new
                    </Button>
                </div>
            </header>

            {children}
        </div>
    );
};

export default ClusterLayoutComponent;
